import { onReady } from "@otto-ec/global-resources/dom"
import { defineEvent } from "@otto-ec/global-resources/nexus";
import { CustomerSessionRefreshed } from "../nexus.js"

const LOCK_NAME = 'fti-session-refresh-lock';
const SESSION_REFRESHED_CHANNEL = 'fti-session-refreshed';
let sessionRefreshedChannel: BroadcastChannel;

class RequestIdStorage {
    private static readonly KEY = 'fti-session-refresh-request-id';

    get(): string {
        const stored = localStorage.getItem(RequestIdStorage.KEY);
        if (stored) {
            return stored;
        }
        const newId = crypto.randomUUID();
        localStorage.setItem(RequestIdStorage.KEY, newId);
        return newId;
    }

    clear(): void {
        localStorage.removeItem(RequestIdStorage.KEY);
    }
}

interface NextRefresh {
    refreshAfterTimestamp: number,
    extendExpiration: boolean
}

class NextRefreshScheduler {
    private static readonly KEY = "fti-session-next-refresh";

    nextDue(): NextRefresh | null {
        const storedNextRefresh = localStorage.getItem(NextRefreshScheduler.KEY)
        if (storedNextRefresh) {
            try {
                const nextRefresh = JSON.parse(storedNextRefresh) as NextRefresh;
                const now = Date.now();
                if (now >= nextRefresh.refreshAfterTimestamp) {
                    return nextRefresh;
                }
            } catch (e) {
                console.error("Error while parsing next refresh from localStorage. Clearing next refresh.", e);
                this.clear()
            }
        }
        return null;
    }

    schedule(seconds: number, extendExpiration: boolean): void {
        const storedRefreshAfter = localStorage.getItem(NextRefreshScheduler.KEY)
        const currentNextRefresh = storedRefreshAfter ? JSON.parse(storedRefreshAfter) as NextRefresh : null;
        const now = Date.now();
        const requestedNextRefresh = now + (seconds * 1000);
        const newNextRefresh: NextRefresh = {
            refreshAfterTimestamp: Math.min(requestedNextRefresh, currentNextRefresh?.refreshAfterTimestamp ?? requestedNextRefresh),
            extendExpiration: extendExpiration || (currentNextRefresh?.extendExpiration ?? false)
        }
        localStorage.setItem(NextRefreshScheduler.KEY, JSON.stringify(newNextRefresh));
    }

    clear(): void {
        localStorage.removeItem(NextRefreshScheduler.KEY);
    }

}

const nextRequestScheduler = new NextRefreshScheduler();
const requestIdStorage = new RequestIdStorage();

interface Session {
    refreshAfterSeconds: number
}

async function refreshSessionByOrt(extendExpiration: boolean): Promise<Session | null> {
    const requestId = requestIdStorage.get();
    const url = `/session-maintenance/session/refresh?requestId=${requestId}&extendExpiration=${extendExpiration}`;

    const response = await fetch(url);

    if (response.ok) {
        requestIdStorage.clear();
        const refreshAfter = response.headers.get('Refresh-After');
        if (refreshAfter) {
            return {
                refreshAfterSeconds: parseInt(refreshAfter, 10)
            }
        } else {
            return null;
        }
    } else {
        throw new Error(`Failed to refresh session: ${response.status} ${response.statusText}`);
    }
}

async function refreshIfNeeded() {
    const nextDue = nextRequestScheduler.nextDue()
    if (nextDue != null) {
        await navigator.locks.request(LOCK_NAME, async () => {
            const nextDueInsideLock = nextRequestScheduler.nextDue();
            if (nextDueInsideLock != null) {
                nextRequestScheduler.clear()
                try {
                    const session = await refreshSessionByOrt(nextDueInsideLock.extendExpiration);
                    publishSessionRefreshed(session != null)
                    if (session != null) {
                        nextRequestScheduler.schedule(session.refreshAfterSeconds, false);
                    }
                } catch (e) {
                    console.debug("Refresh session call failed. Scheduling retry.", e);
                    nextRequestScheduler.schedule(5, nextDueInsideLock.extendExpiration);
                }
            }
        });
    }
}

async function checkRefreshNeededPeriodically() {
    await refreshIfNeeded()
    /*                                   */
    setTimeout(checkRefreshNeededPeriodically, 1000)
}

export async function startRefreshByOrt() {
    await navigator.locks.request(LOCK_NAME, () => nextRequestScheduler.schedule(0, true));
    await checkRefreshNeededPeriodically()
}
function publishSessionRefreshed(hasCustomerSession: boolean) {
    const event: CustomerSessionRefreshed = { hasCustomerSession }
    otto.customerSession.onCustomerSessionRefresh.set(event)
    sessionRefreshedChannel.postMessage(event)
}

export function init() {
    otto.customerSession.assignNamespace({
        onCustomerSessionRefresh: defineEvent(),
    });
    sessionRefreshedChannel = new BroadcastChannel(SESSION_REFRESHED_CHANNEL)
    sessionRefreshedChannel.onmessage = ((event: MessageEvent<CustomerSessionRefreshed>) => {
        otto.customerSession.onCustomerSessionRefresh.set(event.data)
    })
    onReady.subscribe(startRefreshByOrt)
}


